import PropTypes from 'prop-types';
import styled from 'styled-components';
import { variant as styledVariant } from 'styled-system';
import css from '@styled-system/css';

import {
  contentfulLcArticlePreview,
  contentfulLcCalculatorPreview,
  contentfulLcGuidePreview,
} from 'types';
import getContentfulImgixUrl from 'utils/getContentfulImgixUrl';
import getContentTypeLabel from 'utils/getContentTypeLabel';
import { track } from 'utils/analytics';
import Box from 'legacy/components/box';
import Image from 'legacy/components/image';
import Heading from 'legacy/components/heading';
import Text from 'legacy/components/text';
import Link from 'legacy/components/link';
import Pill from 'legacy/components/pill';

const StyledImage = styled(Image)(
  css({
    position: 'relative',
    width: '100%',
    borderRadius: 'large',
    '& img': {
      position: 'absolute',
      top: 0,
      left: 0,
      width: '100%',
      height: '100%',
      borderWidth: 1,
      borderColor: 'gray100',
      borderStyle: 'solid',
      borderRadius: 'large',
    },
  }),
  styledVariant({
    variants: {
      default: {
        pb: '52.77%',
      },
      large: {
        pb: '50.7%',
      },
    },
  })
);

const Title = styled(Heading)(
  css({
    mt: 3,
    fontSize: [4, null, null, 3, 4],
    fontWeight: 700,
    '& > a': {
      fontWeight: 700,
    },
  })
);

const ReadingLength = styled(Text)(
  css({
    mt: 3,
    fontWeight: 600,
    color: 'blue',
    textTransform: 'uppercase',
    letterSpacing: 0.05,
  })
);

const ResourcePreview = ({
  variant = 'default',
  resource,
  showResourcePill = false,
  pillContent = null,
}) => {
  const urlPaths = {
    LearningCenterArticle: '/learn/articles',
    LearningCenterCalculator: '/learn/calculators',
    LearningCenterGuide: '/learn/guides',
    CustomerStory: '/customer-stories',
  };

  const resourceUrl =
    resource.url ?? `${urlPaths[resource.__typename]}/${resource.slug}/`;

  const handleImageClick = (url, src) =>
    track('clicked_image_link', {
      target_url: url,
      img_src: src,
    });

  return (
    <Box as="article">
      <Link
        href={resourceUrl}
        title={resource.title}
        onClick={() => handleImageClick(resourceUrl, resource.image?.url)}
      >
        {variant === 'large' && (
          <StyledImage
            src={getContentfulImgixUrl(
              resource.image?.url ?? process.env.NEXT_PUBLIC_BLOG_FALLBACK_IMAGE
            )}
            additionalParams={{ fit: 'crop' }}
            htmlAttributes={{ alt: resource.title }}
            width={789}
            height={400}
            variant={variant}
            lazyload
          />
        )}
        {variant === 'default' && (
          <StyledImage
            src={getContentfulImgixUrl(
              resource.image?.url ?? process.env.NEXT_PUBLIC_BLOG_FALLBACK_IMAGE
            )}
            additionalParams={{ fit: 'crop' }}
            htmlAttributes={{ alt: resource.title }}
            width={379}
            height={200}
            variant={variant}
            lazyload
          />
        )}
      </Link>
      {showResourcePill && (
        <Pill>{pillContent ?? getContentTypeLabel(resource.__typename)}</Pill>
      )}
      {!showResourcePill && resource.readingLength && (
        <ReadingLength>{resource.readingLength} Min Read</ReadingLength>
      )}
      <Title as="h3">
        <Link href={resourceUrl}>{resource.title}</Link>
      </Title>
      {variant === 'large' && <Text color="blue">{resource.excerpt}</Text>}
    </Box>
  );
};

ResourcePreview.propTypes = {
  variant: PropTypes.oneOf(['default', 'large']),
  resource: PropTypes.oneOfType([
    contentfulLcArticlePreview,
    contentfulLcCalculatorPreview,
    contentfulLcGuidePreview,
  ]).isRequired,
  showResourcePill: PropTypes.bool,
  pillContent: PropTypes.string,
};

export default ResourcePreview;
